import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import validator from 'validator';
import { CustomPlaceholder, Offer } from '../campaign-builder.types';
import { coerceOfferFieldToString, getOfferFieldInUse } from '../offer-field.utils';

interface Props {
  productGroup: ProductGroups;

  perplexityUrl: string;
  perplexityLoading: boolean;
  perplexityError: {
    noJobAd: boolean;
    noJobTitle: boolean;
    noProblems: boolean;
    noFeatures: boolean;
    noBenefits: boolean;
    noImpact: boolean;
  };
  perplexityStatusMessage: string;
  offer: Offer;

  customPlaceholders: CustomPlaceholder[];
  handleAddCustomPlaceholder(title: string, text: string): void;
  handleDeleteCustomPlaceholder(key: string): void;
  handleEditCustomPlaceholder(originalKey: string, newKey: string, newValue: string): void;

  handlePerplexityUrlChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleOfferInputChange(type: keyof Offer): (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePerplexityPaste(event: React.ClipboardEvent): void;
  handlePerplexityKeyDown(event: React.KeyboardEvent): void;
  handlePerplexityRefresh(): void;

  handleOfferToggleChange(type: keyof Offer): (inUse: boolean) => void;
}

const BriefAi: React.FC<Props> = ({
  productGroup,
  offer,
  customPlaceholders,
  handleAddCustomPlaceholder,
  handleDeleteCustomPlaceholder,
  handleEditCustomPlaceholder,
  perplexityUrl,
  perplexityLoading,
  perplexityError,
  perplexityStatusMessage,
  handlePerplexityUrlChange,
  handleOfferInputChange,
  handlePerplexityPaste,
  handlePerplexityKeyDown,
  handlePerplexityRefresh,
  handleOfferToggleChange,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [placeholderKey, setPlaceholderKey] = useState('');
  const [placeholderValue, setPlaceholderValue] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editKeyInput, setEditKeyInput] = useState('');
  const [editValueInput, setEditValueInput] = useState('');

  /**
   * State to track whether the Custom Placeholders accordion is expanded. We use
   * this to decide if the field toggles (Switches) should be shown or hidden.
   * They are displayed only when this menu is open as requested.
   */
  const [customPlaceholdersExpanded, setCustomPlaceholdersExpanded] = useState<boolean>(customPlaceholders.length > 0);

  /** Handler that updates the accordion expansion state */
  const handleCustomPlaceholdersAccordionChange = (_event: React.SyntheticEvent, expanded: boolean) => {
    setCustomPlaceholdersExpanded(expanded);
  };

  const handleSavePlaceholder = () => {
    handleAddCustomPlaceholder(placeholderKey.trim(), placeholderValue.trim());
    setPlaceholderKey('');
    setPlaceholderValue('');
  };

  // Helper function to handle product-specific translations
  // Only used for campaignBriefingExplanation & equipAiDescription
  const getLocalizedText = (key: string): string => {
    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      const leadBoosterKey = `${key}_LeadBooster`;
      return t(leadBoosterKey) || t(key);
    }
    // Default is for Active Sourcing
    const activeSourcingKey = `${key}_ActiveSourcing`;
    return t(activeSourcingKey) || t(key);
  };

  const isUrlValid = useMemo(() => {
    if (!perplexityUrl) return true;
    return validator.isURL(perplexityUrl);
  }, [perplexityUrl]);

  const normalizedOffer = {
    jobTitle: coerceOfferFieldToString(offer?.jobTitle),
    jobOffer: coerceOfferFieldToString(offer?.jobOffer),
    problems: coerceOfferFieldToString(offer?.problems),
    features: coerceOfferFieldToString(offer?.features),
    benefits: coerceOfferFieldToString(offer?.benefits),
    impact: coerceOfferFieldToString(offer?.impact),
  };

  const fieldInUse = {
    jobTitle: getOfferFieldInUse(offer?.jobTitle),
    jobOffer: getOfferFieldInUse(offer?.jobOffer),
    problems: getOfferFieldInUse(offer?.problems),
    features: getOfferFieldInUse(offer?.features),
    benefits: getOfferFieldInUse(offer?.benefits),
    impact: getOfferFieldInUse(offer?.impact),
  };

  // Determine if the field toggles should be visible. They should only be
  // shown when the custom placeholders accordion is expanded, independent of
  // the selected product group.
  const showFieldToggles = customPlaceholdersExpanded;

  /**
   * Automatically open the accordion when either
   * 1) the user has created at least one custom placeholder, or
   * 2) at least one of the predefined placeholders is toggled off (not in use).
   * This guarantees that the user immediately sees the section relevant for
   * managing placeholders whenever it becomes important.
   */
  const anyPredefinedPlaceholderToggledOff = Object.values(fieldInUse).some((v) => !v);

  const accordionLocked = customPlaceholders.length > 0 || anyPredefinedPlaceholderToggledOff;

  useEffect(() => {
    if (!customPlaceholdersExpanded && (customPlaceholders.length > 0 || anyPredefinedPlaceholderToggledOff)) {
      setCustomPlaceholdersExpanded(true);
    }
  }, [customPlaceholdersExpanded, customPlaceholders.length, anyPredefinedPlaceholderToggledOff]);

  return (
    <Stack gap={2}>
      <section className={classes.section}>
        <Typography className={classes.blueTitle}>{t('campaignBriefingTitle')}</Typography>
        <Typography className={classes.title}>{t('campaignBriefingSubtitle')}</Typography>
        {/* Use getLocalizedText for campaignBriefingExplanation */}
        <Typography className={classes.greyText}>{getLocalizedText('campaignBriefingExplanation')}</Typography>
      </section>

      <Stack gap={1}>
        <Typography className={classes.formTitle}>{t('equipAiTitle')}</Typography>
        {/* Use getLocalizedText for equipAiDescription */}
        <Typography className={classes.formText}>{getLocalizedText('equipAiDescription')}</Typography>
      </Stack>

      <Stack direction="row" gap={1} alignItems="center">
        <PaiTextField
          value={perplexityUrl}
          placeholder={productGroup === ProductGroups.HR_BOOSTER ? t('jobOfferUrlPlaceholder') : t('productUrlPlaceholder')}
          onChange={handlePerplexityUrlChange}
          onKeyDown={isUrlValid ? handlePerplexityKeyDown : undefined}
          onPaste={isUrlValid ? handlePerplexityPaste : undefined}
          loading={perplexityLoading}
          disabled={perplexityLoading}
          error={!isUrlValid}
          errorText={t('invalidPerplexityUrlError')}
        />
        <IconButton
          className={classes.refreshButton}
          size="small"
          onClick={handlePerplexityRefresh}
          disabled={perplexityLoading || !isUrlValid}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>

      {perplexityLoading && (
        <Box display={'flex'} justifyContent={'center'}>
          <Typography className={classes.statusMessage}>{perplexityStatusMessage || t('processingUrl')}</Typography>
        </Box>
      )}

      {productGroup === ProductGroups.HR_BOOSTER ? (
        <>
          {/* Job Title */}
          <Stack gap={0.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography className={classes.formTitle}>{t('jobTitle')}</Typography>
              {showFieldToggles && (
                <Switch size="small" checked={fieldInUse.jobTitle} onChange={(_, checked) => handleOfferToggleChange('jobTitle')(checked)} />
              )}
            </Stack>
            {fieldInUse.jobTitle && (
              <PaiTextField
                value={normalizedOffer.jobTitle}
                placeholder={t('jobTitlePlaceholder')}
                onChange={handleOfferInputChange('jobTitle')}
                disabled={perplexityLoading}
                error={perplexityError.noJobTitle}
                errorText={t('jobTitleErrorText')}
              />
            )}
          </Stack>

          {/* Job Offer */}
          <Stack gap={0.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography className={classes.formTitle}>{t('jobOffer')}</Typography>
              {showFieldToggles && (
                <Switch size="small" checked={fieldInUse.jobOffer} onChange={(_, checked) => handleOfferToggleChange('jobOffer')(checked)} />
              )}
            </Stack>
            {fieldInUse.jobOffer && (
              <PaiTextArea
                value={normalizedOffer.jobOffer}
                rows={5}
                placeholder={t('jobOfferPlaceholder')}
                onChange={handleOfferInputChange('jobOffer')}
                disabled={perplexityLoading}
                error={perplexityError.noJobAd}
                errorText={t('jobAdErrorText')}
              />
            )}
          </Stack>
        </>
      ) : (
        <>
          {(['problems', 'features', 'benefits', 'impact'] as Array<keyof Offer>).map((key) => {
            const labelKey = key as 'problems' | 'features' | 'benefits' | 'impact';
            const translation = t(labelKey);
            const placeholderMap: Record<string, string> = {
              problems: t('productProblemsPlaceholder'),
              features: t('productFeaturesPlaceholder'),
              benefits: t('productBenefitsPlaceholder'),
              impact: t('productImpactPlaceholder'),
            };
            const errorMap: Record<string, boolean> = {
              problems: perplexityError.noProblems,
              features: perplexityError.noFeatures,
              benefits: perplexityError.noBenefits,
              impact: perplexityError.noImpact,
            };
            const errorTextMap: Record<string, string> = {
              problems: t('productProblemsErrorText'),
              features: t('productFeaturesErrorText'),
              benefits: t('productBenefitsErrorText'),
              impact: t('productImpactErrorText'),
            };

            return (
              <Stack key={key} gap={0.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography className={classes.formTitle}>{translation}</Typography>
                  {showFieldToggles && (
                    <Switch size="small" checked={fieldInUse[labelKey]} onChange={(_, checked) => handleOfferToggleChange(labelKey)(checked)} />
                  )}
                </Stack>
                {fieldInUse[labelKey] && (
                  <PaiTextArea
                    value={normalizedOffer[labelKey] as string}
                    placeholder={placeholderMap[labelKey]}
                    onChange={handleOfferInputChange(labelKey)}
                    disabled={perplexityLoading}
                    error={errorMap[labelKey]}
                    errorText={errorTextMap[labelKey]}
                  />
                )}
              </Stack>
            );
          })}
        </>
      )}

      {/* Custom Placeholders Accordion */}
      <Accordion
        expanded={customPlaceholdersExpanded}
        onChange={!accordionLocked ? handleCustomPlaceholdersAccordionChange : undefined}
        disableGutters
        square
        className={classes.subtleAccordion}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.subtleAccordionSummary}>
          <Typography className={classes.formTitle}>{t('customPlaceholders')}</Typography>
          {accordionLocked && (
            <Tooltip title={t('customPlaceholdersLockedTooltip')} arrow placement="right">
              <InfoOutlinedIcon fontSize="small" sx={{ ml: 1 }} />
            </Tooltip>
          )}
        </AccordionSummary>
        <AccordionDetails className={classes.subtleAccordionDetails}>
          <Typography className={classes.greyText} mb={1}>
            {t('customPlaceholdersDescription')}
          </Typography>

          <Stack gap={1} mb={2}>
            {customPlaceholders.map((p) => (
              <Box key={p.key} className={classes.placeholderItem}>
                {editingKey === p.key ? (
                  <Stack gap={1}>
                    <PaiTextField label={t('placeholderKey')} value={editKeyInput} onChange={(e) => setEditKeyInput(e.target.value)} />
                    <PaiTextArea label={t('placeholderValue')} value={editValueInput} rows={4} onChange={(e) => setEditValueInput(e.target.value)} />
                    <Stack direction="row" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleEditCustomPlaceholder(p.key, editKeyInput, editValueInput);
                          setEditingKey(null);
                        }}
                      >
                        <CheckIcon fontSize="small" color="success" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setEditingKey(null)}>
                        <CloseIcon fontSize="small" color="error" />
                      </IconButton>
                    </Stack>
                  </Stack>
                ) : (
                  <>
                    <Typography fontWeight="bold">{p.key}</Typography>
                    <Typography>{p.value}</Typography>
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', top: 0, right: 24 }}
                      onClick={() => {
                        setEditingKey(p.key);
                        setEditKeyInput(p.key);
                        setEditValueInput(p.value);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ position: 'absolute', top: 0, right: 0 }} onClick={() => handleDeleteCustomPlaceholder(p.key)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            ))}
          </Stack>
          <Stack gap={1}>
            <PaiTextField label={t('placeholderKey')} value={placeholderKey} onChange={(e) => setPlaceholderKey(e.target.value)} />
            <PaiTextArea label={t('placeholderValue')} value={placeholderValue} rows={4} onChange={(e) => setPlaceholderValue(e.target.value)} />
            <Box sx={{ alignSelf: 'flex-start' }}>
              <PaiButton
                text={t('savePlaceholder')}
                border="rounded"
                size="small"
                action={handleSavePlaceholder}
                disabled={!placeholderKey.trim() || !placeholderValue.trim()}
              />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  section: {
    paddingBottom: '16px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  blueTitle: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  formTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  formText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  statusMessage: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.main,
    textAlign: 'center',
  },
  subtleAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&::before': { display: 'none' },
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '10px',
  },
  subtleAccordionSummary: {
    minHeight: 40,
    '& .MuiAccordionSummary-content': { marginTop: 0, marginBottom: 0 },
  },
  subtleAccordionDetails: {
    paddingTop: 0,
    backgroundColor: 'transparent',
  },
  placeholderItem: {
    position: 'relative',
    backgroundColor: theme.palette.greys.lightest,
    padding: 8,
    borderRadius: 4,
    border: `1px solid ${theme.palette.greys.lighter}`,
    boxShadow: 'none',
  },
  refreshButton: {
    backgroundColor: theme.palette.greys.lightest,
    '&:hover': {
      backgroundColor: theme.palette.greys.lighter,
    },
  },
}));

export default BriefAi;
